import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../utils/error-handler';
import { logBoundaryError } from '../utils/error-logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
}

/**
 * アプリケーション全体をラップするグローバルエラーバウンダリ
 * エラーロギング、ユーザーフレンドリーなエラー表示、回復機能を提供
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private readonly maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // エラーロギングサービスにログを送信
    logBoundaryError(error, errorInfo, 'GlobalErrorBoundary');

    // 既存のエラーレポート機能も保持
    reportError(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'GlobalErrorBoundary',
        retryCount: this.retryCount,
        errorId: this.state.errorId,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    });

    // カスタムエラーハンドラーがある場合は実行
    this.props.onError?.(error, errorInfo);

    // 開発環境では詳細をコンソールに出力
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 GlobalErrorBoundary - Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Error ID:', this.state.errorId);
      console.groupEnd();
    }
  }

  override componentDidUpdate(prevProps: Props) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // propsの変更でリセットする場合
    if (hasError && prevProps.resetKeys !== resetKeys && resetOnPropsChange) {
      if (resetKeys) {
        const hasResetKeyChanged = resetKeys.some(
          (key, index) => prevProps.resetKeys?.[index] !== key,
        );
        if (hasResetKeyChanged) {
          this.resetErrorBoundary();
        }
      }
    }
  }

  private resetErrorBoundary = () => {
    this.retryCount = 0;
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
    });
  };

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.resetErrorBoundary();
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private getErrorSeverity(): 'low' | 'medium' | 'high' {
    if (!this.state.error) return 'medium';

    const errorMessage = this.state.error.message.toLowerCase();
    const errorStack = this.state.error.stack?.toLowerCase() || '';

    // 高重要度エラー
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('authentication') ||
      errorStack.includes('app.tsx') ||
      errorStack.includes('index.tsx')
    ) {
      return 'high';
    }

    // 低重要度エラー
    if (
      errorMessage.includes('warning') ||
      errorMessage.includes('deprecated') ||
      this.retryCount > 0
    ) {
      return 'low';
    }

    return 'medium';
  }

  override render() {
    if (this.state.hasError) {
      // カスタムフォールバックコンポーネントがある場合は使用
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const severity = this.getErrorSeverity();
      const canRetry = this.retryCount < this.maxRetries;

      // グローバルエラーUI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-lg w-full space-y-8">
            <div className="text-center">
              {/* エラー重要度に応じたアイコン */}
              <div
                className={`mx-auto h-16 w-16 ${
                  severity === 'high'
                    ? 'text-red-600'
                    : severity === 'medium'
                      ? 'text-yellow-600'
                      : 'text-orange-500'
                }`}
              >
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label={`エラー ${severity === 'high' ? '重大' : severity === 'medium' ? '中' : '軽微'}`}
                >
                  <title>エラー発生</title>
                  {severity === 'high' ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  )}
                </svg>
              </div>

              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                {severity === 'high'
                  ? '重大なエラーが発生しました'
                  : severity === 'medium'
                    ? 'エラーが発生しました'
                    : '一時的な問題が発生しました'}
              </h2>

              <p className="mt-2 text-center text-sm text-gray-600">
                {severity === 'high'
                  ? 'システムに重大な問題が発生しています。サポートにお問い合わせください。'
                  : severity === 'medium'
                    ? '予期しないエラーが発生しました。以下の方法でお試しください。'
                    : '一時的な問題です。しばらく待ってから再試行してください。'}
              </p>

              {/* エラーID表示 */}
              {this.state.errorId && (
                <p className="mt-1 text-xs text-gray-500">エラーID: {this.state.errorId}</p>
              )}
            </div>

            {/* 開発環境でのエラー詳細 */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <h3 className="text-sm font-medium text-red-800 mb-2">開発モード - エラー詳細:</h3>
                <p className="text-xs text-red-700 font-mono break-all mb-2">
                  <strong>Message:</strong> {this.state.error.message}
                </p>
                <p className="text-xs text-red-700 font-mono break-all mb-2">
                  <strong>Name:</strong> {this.state.error.name}
                </p>
                {this.state.error.stack && (
                  <div className="mt-2">
                    <p className="text-xs text-red-700 font-medium mb-1">Stack Trace:</p>
                    <pre className="text-xs text-red-600 overflow-auto max-h-32 bg-red-100 p-2 rounded">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
                {this.state.errorInfo?.componentStack && (
                  <div className="mt-2">
                    <p className="text-xs text-red-700 font-medium mb-1">Component Stack:</p>
                    <pre className="text-xs text-red-600 overflow-auto max-h-24 bg-red-100 p-2 rounded">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* アクションボタン */}
            <div className="space-y-3">
              {canRetry && (
                <button
                  type="button"
                  onClick={this.handleRetry}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  再試行 {this.retryCount > 0 && `(${this.retryCount}/${this.maxRetries})`}
                </button>
              )}

              <button
                type="button"
                onClick={this.handleReload}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ページを再読み込み
              </button>

              <button
                type="button"
                onClick={this.handleGoHome}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ホームページに戻る
              </button>
            </div>

            {/* サポート情報 */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                問題が続く場合は、上記のエラーIDと一緒にサポートにお問い合わせください。
              </p>
              {severity === 'high' && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ このエラーは自動的に開発チームに報告されます。
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * より軽量なローカルエラーバウンダリ（既存のSimpleErrorBoundaryを拡張）
 */
export const LocalErrorBoundary: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
}> = ({ children, fallback, name = 'LocalErrorBoundary' }) => {
  return (
    <GlobalErrorBoundary
      fallback={
        fallback || (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label="エラーアイコン"
                >
                  <title>エラー</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">この部分でエラーが発生しました</h3>
                <p className="mt-1 text-sm text-red-700">ページを再読み込みしてお試しください。</p>
              </div>
            </div>
          </div>
        )
      }
      onError={(error, errorInfo) => {
        logBoundaryError(error, errorInfo, name);
      }}
    >
      {children}
    </GlobalErrorBoundary>
  );
};
